import { GrahpQLTrackingRequester, type TrackingGraphQL, type TrackingSpeedEvents } from '@cobliteam/api-lib';
import type { VehicleTrackingSegmentType } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { EventTrigger, Media, MediaType, TimestampInMillis, UUID } from '@cobliteam/common-types';
import { type UseQueryOptions, useQuery } from '@tanstack/react-query';

// Lazy instantiation to avoid circular dependency issues
const getGraphQLTrackingRequester = () => new GrahpQLTrackingRequester();

const QUERY_KEY = 'tracking';

export type TrackingSegmentRequest = {
  mediaId?: string;
  vehicleId: UUID;
  startTime: TimestampInMillis;
  endTime: TimestampInMillis;
  segmentStart: TimestampInMillis;
  segmentEnd: TimestampInMillis;
  allowUnfinishedStep: boolean;
  match?: boolean;
  eventTriggers?: EventTrigger[];
  mediaTypes?: MediaType[];
  enabled?: boolean;
};

const useTracking = () => {
  const useTrackingEssencialInfo = (
    vehicleId: UUID,
    startTimestamp: TimestampInMillis,
    endTimestamp: TimestampInMillis,
    useMapMatch: boolean,
    enabled = false,
    withSpeed = false
  ) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<TrackingGraphQL>({
      queryKey: [`${QUERY_KEY}-stops`, vehicleId, startTimestamp, endTimestamp, useMapMatch, withSpeed],
      queryFn: () => getGraphQLTrackingRequester().getTrackingEssencialInfo(vehicleId, startTimestamp, endTimestamp, useMapMatch, withSpeed),
      refetchOnMount: false,
      enabled,
    });
  };

  const useTrackingSpeedEvents = (vehicleId: UUID, startTimestamp: TimestampInMillis, endTimestamp: TimestampInMillis, useMapMatch: boolean, enabled = true) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<TrackingSpeedEvents>({
      queryKey: [`${QUERY_KEY}-speed-events`, vehicleId, startTimestamp, endTimestamp, useMapMatch],
      queryFn: () => getGraphQLTrackingRequester().getTrackingSpeedEvents(vehicleId, startTimestamp, endTimestamp, useMapMatch),
      refetchOnMount: false,
      enabled,
      select(data) {
        return {
          ...data,
          roadSpeedEvents: data.roadSpeedEvents.map((event) => ({ ...event, type: 'road' as const })),
          vehicleSpeedEvents: data.vehicleSpeedEvents.map((event) => ({ ...event, type: 'vehicle' as const })),
        };
      },
    });
  };

  return {
    useTrackingEssencialInfo,
    useTrackingSpeedEvents,
  };
};

const useGetTracking = (
  vehicleId: UUID,
  startTimestamp: TimestampInMillis,
  endTimestamp: TimestampInMillis,
  useMapMatch: boolean,
  options?: Partial<UseQueryOptions<TrackingGraphQL>>
) => {
  return useQuery<TrackingGraphQL>({
    queryKey: [QUERY_KEY, vehicleId, startTimestamp, endTimestamp, useMapMatch],
    queryFn: () => getGraphQLTrackingRequester().getFullTrackingInfo(vehicleId, startTimestamp, endTimestamp, useMapMatch),
    ...options,
  });
};

const useGetTrackingSegment = ({
  vehicleId,
  startTime,
  endTime,
  segmentStart,
  segmentEnd,
  allowUnfinishedStep,
  match = true,
  eventTriggers = [],
  mediaTypes = [],
  enabled = true,
}: TrackingSegmentRequest) => {
  return useQuery<Omit<VehicleTrackingSegmentType, 'mediaEvents'> & { mediaEvents: Media[] }>({
    queryKey: [`${QUERY_KEY}-route-segment`, vehicleId, segmentStart, segmentEnd],
    queryFn: () =>
      getGraphQLTrackingRequester().getVehicleTrackingSegment(vehicleId, startTime, endTime, segmentStart, segmentEnd, allowUnfinishedStep, match, eventTriggers, mediaTypes),
    refetchOnMount: false,
    retry: false,
    enabled,
  });
};

export default useTracking;
export { useGetTracking, useGetTrackingSegment };
